import { useState } from 'react';
import { useNavigate, Navigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import JoditEditor from 'jodit-react';
import { ArrowLeft, Save, Image as ImageIcon, X, Loader2 } from 'lucide-react';
import { Navbar } from '@/components/layout/Navbar';
import { Button } from '@/components/ui/button';
import { useAuthStore } from '@/stores/auth.store';
import { useCreateBlogPost, useBlogCategories, useUploadImage } from '@/hooks/useBlog';
import { showError, showSuccess } from '@/lib/toast';

export function CreateBlog() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { user } = useAuthStore();
  const createPost = useCreateBlogPost();
  const {
    data: categories,
    isLoading: categoriesLoading,
    isError: categoriesError,
  } = useBlogCategories();
  const uploadImage = useUploadImage();

  const [title, setTitle] = useState('');
  const [content, setContent] = useState('');
  const [excerpt, setExcerpt] = useState('');
  const [coverImage, setCoverImage] = useState('');
  const [categoryId, setCategoryId] = useState('');
  const [tagsInput, setTagsInput] = useState('');
  const [status, setStatus] = useState<'draft' | 'published'>('draft');

  if (user?.role !== 'ADMIN') {
    return <Navigate to="/blog" replace />;
  }

  const handleImageUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    try {
      const result = await uploadImage.mutateAsync(file);
      if (!result?.url) {
        showError('Upload succeeded but no URL was returned');
        return;
      }
      setCoverImage(result.url);
      showSuccess('Cover image uploaded');
    } catch (err: unknown) {
      const message =
        (err as { response?: { data?: { message?: string } }; message?: string })
          ?.response?.data?.message ||
        (err as { message?: string })?.message ||
        'Image upload failed';
      showError(message);
    } finally {
      // Reset the input so selecting the same file again still triggers onChange
      e.target.value = '';
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!title.trim() || !content.trim()) {
      showError('Title and content are required');
      return;
    }
    if (!categoryId) {
      showError('Please select a category');
      return;
    }

    try {
      const tags = tagsInput
        .split(',')
        .map((t) => t.trim())
        .filter(Boolean);

      await createPost.mutateAsync({
        title,
        content,
        excerpt: excerpt || undefined,
        coverImage: coverImage || undefined,
        categoryId,
        tags,
        status,
      });
      showSuccess('Post created successfully');
      navigate('/blog/my-posts');
    } catch (err: unknown) {
      const axiosErr = err as {
        response?: { data?: { message?: string | string[] } };
        message?: string;
      };
      const backendMessage = axiosErr?.response?.data?.message;
      const message = Array.isArray(backendMessage)
        ? backendMessage.join(', ')
        : backendMessage || axiosErr?.message || 'Failed to create post';
      showError(message);
    }
  };

  const joditConfig = {
    readonly: false,
    height: 500,
    toolbarAdaptive: false,
    buttons: [
      'bold', 'italic', 'underline', 'strikethrough', '|',
      'ul', 'ol', '|',
      'font', 'fontsize', 'brush', '|',
      'image', 'link', 'table', '|',
      'align', 'undo', 'redo', '|',
      'hr', 'eraser', 'fullsize', 'source',
    ],
  };

  return (
    <div className="min-h-screen bg-background">
      <Navbar />
      <div className="container mx-auto px-4 py-8">
        <div className="mx-auto max-w-4xl">
          <button
            onClick={() => navigate(-1)}
            className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground mb-6 transition-colors"
          >
            <ArrowLeft className="h-4 w-4" />
            {t('common.back')}
          </button>

          <h1 className="text-2xl font-bold mb-8">{t('blogPage.createPost')}</h1>

          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Title */}
            <div>
              <label className="block text-sm font-medium mb-1.5">{t('blogPage.postTitle')}</label>
              <input
                type="text"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                placeholder={t('blogPage.postTitlePlaceholder')}
                className="w-full rounded-lg border bg-background px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500"
                required
              />
            </div>

            {/* Excerpt */}
            <div>
              <label className="block text-sm font-medium mb-1.5">{t('blogPage.excerpt')}</label>
              <textarea
                value={excerpt}
                onChange={(e) => setExcerpt(e.target.value)}
                placeholder={t('blogPage.excerptPlaceholder')}
                rows={2}
                className="w-full rounded-lg border bg-background px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500 resize-none"
              />
            </div>

            {/* Cover Image */}
            <div>
              <label className="block text-sm font-medium mb-1.5">{t('blogPage.coverImage')}</label>
              {coverImage ? (
                <div className="relative rounded-lg overflow-hidden max-w-md">
                  <img src={coverImage} alt="Cover" className="w-full aspect-video object-cover" />
                  <button
                    type="button"
                    onClick={() => setCoverImage('')}
                    className="absolute top-2 right-2 p-1 bg-black/50 rounded-full text-white hover:bg-black/70"
                  >
                    <X className="h-4 w-4" />
                  </button>
                </div>
              ) : (
                <label
                  className={`flex items-center gap-2 rounded-lg border-2 border-dashed px-4 py-6 text-sm transition-colors justify-center ${
                    uploadImage.isPending
                      ? 'cursor-wait border-emerald-500 text-emerald-600'
                      : 'cursor-pointer text-muted-foreground hover:border-emerald-500 hover:text-emerald-600'
                  }`}
                >
                  {uploadImage.isPending ? (
                    <>
                      <Loader2 className="h-5 w-5 animate-spin" />
                      Uploading...
                    </>
                  ) : (
                    <>
                      <ImageIcon className="h-5 w-5" />
                      {t('blogPage.uploadCoverImage')}
                    </>
                  )}
                  <input
                    type="file"
                    accept="image/*"
                    onChange={handleImageUpload}
                    disabled={uploadImage.isPending}
                    className="hidden"
                  />
                </label>
              )}
            </div>

            {/* Category */}
            <div>
              <label className="block text-sm font-medium mb-1.5">{t('blogPage.category')}</label>
              <select
                value={categoryId}
                onChange={(e) => setCategoryId(e.target.value)}
                disabled={categoriesLoading}
                className="w-full rounded-lg border bg-background px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500 disabled:opacity-60"
              >
                <option value="">
                  {categoriesLoading
                    ? 'Loading categories...'
                    : categoriesError
                      ? 'Failed to load categories'
                      : t('blogPage.selectCategory')}
                </option>
                {categories?.map((cat) => (
                  <option key={cat.id} value={cat.id}>
                    {cat.name}
                  </option>
                ))}
              </select>
              {categoriesError && (
                <p className="mt-1 text-xs text-red-600">
                  Could not load categories. Check your backend and refresh.
                </p>
              )}
            </div>

            {/* Tags */}
            <div>
              <label className="block text-sm font-medium mb-1.5">{t('blogPage.tags')}</label>
              <input
                type="text"
                value={tagsInput}
                onChange={(e) => setTagsInput(e.target.value)}
                placeholder={t('blogPage.tagsPlaceholder')}
                className="w-full rounded-lg border bg-background px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500"
              />
            </div>

            {/* Content Editor */}
            <div>
              <label className="block text-sm font-medium mb-1.5">{t('blogPage.content')}</label>
              <JoditEditor
                value={content}
                config={joditConfig}
                onBlur={(newContent) => setContent(newContent)}
              />
            </div>

            {/* Status & Submit */}
            <div className="flex items-center justify-between pt-4 border-t">
              <div className="flex items-center gap-3">
                <label className="text-sm font-medium">{t('blogPage.status')}:</label>
                <select
                  value={status}
                  onChange={(e) => setStatus(e.target.value as 'draft' | 'published')}
                  className="rounded-lg border bg-background px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500"
                >
                  <option value="draft">{t('blogPage.draft')}</option>
                  <option value="published">{t('blogPage.published')}</option>
                </select>
              </div>
              <Button
                type="submit"
                disabled={createPost.isPending || !title.trim()}
                className="gap-2"
              >
                <Save className="h-4 w-4" />
                {createPost.isPending ? t('common.loading') : t('blogPage.publish')}
              </Button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}
